import type { DialogControlProps } from '@_/ui';
import clsx from 'clsx';
import { useState } from 'react';
import { PiCaretLeftBold, PiCaretRightBold, PiXBold } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { buttonCommonI18n } from '@_/i18n-common';
import {
  Carousel,
  CarouselNextButton,
  CarouselPrevButton,
  CarouselSlide,
  CarouselSlides,
  DialogPrimitive,
  DialogPrimitiveClose,
  DialogPrimitiveContent,
  DialogPrimitiveDescription,
  DialogPrimitivePortal,
  DialogPrimitiveTitle,
  Image
} from '@_/ui';

export interface ImageDialogProps extends DialogControlProps {
  initialIndex?: number;
  imageUrls: string[];
}

export function ImageDialog({ initialIndex = 0, imageUrls, ...dialogProps }: ImageDialogProps) {
  const [index, setIndex] = useState<number>(initialIndex);

  const navButtonStyles = clsx(
    'z-1 flex size-12 items-center justify-center rounded-full border-2 border-white/60 text-white transition-colors duration-150 ease-out hover:bg-white/20 motion-reduce:transition-none'
  );

  return (
    <DialogPrimitive {...dialogProps}>
      <DialogPrimitivePortal>
        <DialogPrimitiveContent
          animation="scale"
          className="inset-0 bg-black"
          disableSwipeDismissal
        >
          <DialogPrimitiveTitle className="hidden" />
          <DialogPrimitiveDescription className="hidden" />

          <Carousel initialIndex={initialIndex} onChange={setIndex}>
            <div className="flex h-screen max-h-screen flex-col items-stretch pb-24">
              <div className="absolute inset-0 hidden flex-col justify-center md:flex">
                <div className="mx-6 flex justify-between">
                  <CarouselPrevButton
                    behavior="instant"
                    className={clsx(navButtonStyles, 'left-3')}
                  >
                    <PiCaretLeftBold className="size-4" />
                  </CarouselPrevButton>
                  <CarouselNextButton
                    behavior="instant"
                    className={clsx(navButtonStyles, 'right-3')}
                  >
                    <PiCaretRightBold className="size-4" />
                  </CarouselNextButton>
                </div>
              </div>

              <header className="flex h-20 shrink-0 flex-col justify-center px-4 text-white md:h-28 md:px-10">
                <div className="relative flex items-center justify-center">
                  <span className="text-base">
                    {index + 1} / {imageUrls.length}
                  </span>

                  <div className="absolute left-0">
                    <DialogPrimitiveClose className="hidden items-center rounded-md px-4 py-2 hover:bg-white/20 md:flex">
                      <PiXBold className="mr-1 size-4.5" />
                      <FormattedMessage {...buttonCommonI18n.close} />
                    </DialogPrimitiveClose>

                    <DialogPrimitiveClose className="flex size-8 shrink-0 items-center md:hidden">
                      <PiXBold className="size-4.5" />
                    </DialogPrimitiveClose>
                  </div>
                </div>
              </header>

              <div className="relative h-[calc(100vh-(--spacing(40)))] grow-0 md:h-[calc(100vh-(--spacing(56)))]">
                <CarouselSlides>
                  {imageUrls.map(url => (
                    <CarouselSlide
                      key={url}
                      className="size-full sm:px-20 md:px-24"
                      render={<Image fit="contain" src={url} />}
                    />
                  ))}
                </CarouselSlides>
              </div>
            </div>
          </Carousel>
        </DialogPrimitiveContent>
      </DialogPrimitivePortal>
    </DialogPrimitive>
  );
}
